/**
 * PaymentAdminSettings — Stripe & Xendit configuration + processor toggle. Super Admin only.
 * All state and handlers live in Settings.js (parent). This component is pure JSX.
 * RULE: Do NOT add state here. Pass new props from Settings.js instead.
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { CreditCard, Key, Loader2 } from 'lucide-react';

export default function PaymentAdminSettings({
  stripeConfig,
  setStripeConfig,
  stripeMasked,
  stripeSaving,
  saveStripeConfig,
  xenditConfig,
  setXenditConfig,
  xenditMasked,
  xenditSaving,
  saveXenditConfig,
  activeProcessor,
  processorSaving,
  processorSwitchBanner,
  setProcessorSwitchBanner,
  switchPaymentProcessor,
}) {
  return (
    <>
      {/* Auto-switch warning banner */}
      {processorSwitchBanner && (
        <div className="flex items-start gap-3 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800" data-testid="processor-switch-banner">
          <span className="mt-0.5 flex-shrink-0 text-amber-500">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
          </span>
          <span className="flex-1">{processorSwitchBanner}</span>
          <button onClick={() => setProcessorSwitchBanner(null)} className="flex-shrink-0 text-amber-500 hover:text-amber-700 transition-colors" aria-label="Dismiss">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
      )}

      {/* Active Processor Toggle */}
      <Card className="border-slate-200" data-testid="processor-toggle-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <CreditCard size={20} className="text-orange-500" />
            Active Payment Processor
          </CardTitle>
          <CardDescription>Select which payment gateway is used for new subscriptions.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-4">
            <div className="flex gap-3">
              {[
                {
                  id: 'stripe',
                  label: 'Stripe',
                  configured: !!stripeMasked.stripe_secret_key_masked,
                },
                {
                  id: 'xendit',
                  label: 'Xendit',
                  configured: !!(xenditMasked.xendit_secret_key_masked && xenditMasked.xendit_webhook_token_masked),
                },
              ].map(({ id: p, label, configured }) => {
                const dbSaysActive = activeProcessor === p;
                const isActive = dbSaysActive && configured;
                const isStaleMismatch = dbSaysActive && !configured;
                const isDisabled = processorSaving || isActive || !configured;
                return (
                  <div key={p} className="flex flex-col items-start gap-1">
                    <button
                      onClick={() => switchPaymentProcessor(p)}
                      disabled={isDisabled}
                      data-testid={`processor-btn-${p}`}
                      title={!configured ? 'Configure API keys before activating' : undefined}
                      className={`flex items-center gap-2 px-5 py-2.5 rounded-lg border text-sm font-medium transition-all duration-150 ${
                        isActive
                          ? 'bg-green-50 border-green-400 text-green-700'
                          : !configured
                            ? 'bg-slate-50 border-slate-200 text-slate-400 cursor-not-allowed opacity-60'
                            : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                      }`}
                    >
                      {processorSaving && !isActive ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : isActive ? (
                        <span className="w-2 h-2 rounded-full bg-green-500" />
                      ) : (
                        <span className="w-2 h-2 rounded-full bg-slate-300" />
                      )}
                      {label}
                      {isActive && <span className="text-xs text-green-600 font-normal">(active)</span>}
                    </button>
                    {isStaleMismatch && (
                      <p className="text-xs text-red-500 pl-1">Configuration missing — processor inactive</p>
                    )}
                    {!configured && !isStaleMismatch && (
                      <p className="text-xs text-amber-600 pl-1">Configure API keys before activating</p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stripe Configuration */}
      <Card className="border-slate-200" data-testid="stripe-config-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <CreditCard size={20} className="text-orange-500" />
            Stripe Configuration
          </CardTitle>
          <CardDescription>
            Manage Stripe API keys. Secrets are encrypted at rest. Fields left blank are not updated.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {(stripeMasked.stripe_secret_key_masked || stripeMasked.stripe_publishable_key) && (
            <div className="rounded-lg bg-slate-50 border border-slate-100 p-4 space-y-2">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Current Keys ({stripeMasked.source === 'db' ? 'DB override' : 'Environment'})</p>
              {stripeMasked.stripe_publishable_key && (
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-slate-500 w-36 flex-shrink-0">Publishable Key</span>
                  <code className="text-slate-700 font-mono text-xs bg-white border border-slate-200 rounded px-2 py-0.5">{stripeMasked.stripe_publishable_key}</code>
                </div>
              )}
              {stripeMasked.stripe_secret_key_masked && (
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-slate-500 w-36 flex-shrink-0">Secret Key</span>
                  <code className="text-slate-700 font-mono text-xs bg-white border border-slate-200 rounded px-2 py-0.5">{stripeMasked.stripe_secret_key_masked}</code>
                </div>
              )}
              {stripeMasked.stripe_webhook_secret_masked && (
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-slate-500 w-36 flex-shrink-0">Webhook Secret</span>
                  <code className="text-slate-700 font-mono text-xs bg-white border border-slate-200 rounded px-2 py-0.5">{stripeMasked.stripe_webhook_secret_masked}</code>
                </div>
              )}
            </div>
          )}
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-slate-700">Publishable Key</Label>
              <Input placeholder="pk_live_... or pk_test_..." value={stripeConfig.stripe_publishable_key} onChange={e => setStripeConfig(p => ({ ...p, stripe_publishable_key: e.target.value }))} data-testid="stripe-publishable-key-input" className="font-mono text-sm" />
              <p className="text-xs text-slate-400">Public key used in the frontend. Safe to display.</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-slate-700">Secret Key</Label>
              <Input type="password" placeholder="sk_live_... (leave blank to keep current)" value={stripeConfig.stripe_secret_key} onChange={e => setStripeConfig(p => ({ ...p, stripe_secret_key: e.target.value }))} data-testid="stripe-secret-key-input" className="font-mono text-sm" />
              <p className="text-xs text-slate-400">Stored encrypted. Never exposed after saving.</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-slate-700">Webhook Secret</Label>
              <Input type="password" placeholder="whsec_... (leave blank to keep current)" value={stripeConfig.stripe_webhook_secret} onChange={e => setStripeConfig(p => ({ ...p, stripe_webhook_secret: e.target.value }))} data-testid="stripe-webhook-secret-input" className="font-mono text-sm" />
              <p className="text-xs text-slate-400">Used to verify Stripe webhook signatures. Stored encrypted.</p>
            </div>
          </div>
          <Button onClick={saveStripeConfig} disabled={stripeSaving} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="stripe-config-save-btn">
            {stripeSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Key size={14} className="mr-2" />}
            Save Stripe Configuration
          </Button>
        </CardContent>
      </Card>

      {/* Xendit Configuration */}
      <Card className="border-slate-200" data-testid="xendit-config-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <CreditCard size={20} className="text-orange-500" />
            Xendit Configuration
          </CardTitle>
          <CardDescription>
            Xendit uses a Secret Key and Webhook Token. Secrets encrypted at rest. Fields left blank are not updated.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {(xenditMasked.xendit_secret_key_masked || xenditMasked.xendit_webhook_token_masked) && (
            <div className="rounded-lg bg-slate-50 border border-slate-100 p-4 space-y-2">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Current Keys (DB)</p>
              {xenditMasked.xendit_secret_key_masked && (
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-slate-500 w-36 flex-shrink-0">Secret Key</span>
                  <code className="text-slate-700 font-mono text-xs bg-white border border-slate-200 rounded px-2 py-0.5">{xenditMasked.xendit_secret_key_masked}</code>
                </div>
              )}
              {xenditMasked.xendit_webhook_token_masked && (
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-slate-500 w-36 flex-shrink-0">Webhook Token</span>
                  <code className="text-slate-700 font-mono text-xs bg-white border border-slate-200 rounded px-2 py-0.5">{xenditMasked.xendit_webhook_token_masked}</code>
                </div>
              )}
            </div>
          )}
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-slate-700">Secret Key</Label>
              <Input type="password" placeholder="xnd_production_... or xnd_development_..." value={xenditConfig.xendit_secret_key} onChange={e => setXenditConfig(p => ({ ...p, xendit_secret_key: e.target.value }))} data-testid="xendit-secret-key-input" className="font-mono text-sm" />
              <p className="text-xs text-slate-400">Your Xendit secret key. Stored encrypted.</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-slate-700">Webhook Token</Label>
              <Input type="password" placeholder="Your Xendit callback verification token" value={xenditConfig.xendit_webhook_token} onChange={e => setXenditConfig(p => ({ ...p, xendit_webhook_token: e.target.value }))} data-testid="xendit-webhook-token-input" className="font-mono text-sm" />
              <p className="text-xs text-slate-400">Used to verify Xendit webhook callbacks. Set in Xendit Dashboard → Webhooks.</p>
            </div>
          </div>
          <Button onClick={saveXenditConfig} disabled={xenditSaving} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="xendit-config-save-btn">
            {xenditSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Key size={14} className="mr-2" />}
            Save Xendit Configuration
          </Button>
        </CardContent>
      </Card>
    </>
  );
}
